import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select } from './ui/select';
import { getQueryFn } from '../lib/queryClient';
import type { Country } from '../types';

interface Scope3FormProps {
  onSubmit: (data: any) => void;
  isSubmitting?: boolean;
  selectedCompanyId?: number;
}

const regions = [
  { value: 'GCC', label: 'GCC' },
  { value: 'US', label: 'US' },
  { value: 'EU', label: 'EU' },
  { value: 'UK', label: 'UK' },
  { value: 'India', label: 'India' },
  { value: 'China', label: 'China' },
  { value: 'Other', label: 'Other' },
];

export function Scope3Form({ onSubmit, isSubmitting = false, selectedCompanyId }: Scope3FormProps) {
  // Fetch company countries
  const { data: companyCountries } = useQuery<Country[]>({
    queryKey: selectedCompanyId ? [`/api/countries/company/${selectedCompanyId}`] : [null],
    queryFn: getQueryFn(),
    enabled: !!selectedCompanyId,
  });

  // Form state
  const [country, setCountry] = useState('');
  const [activityType, setActivityType] = useState('');
  const [unit, setUnit] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reportingYear, setReportingYear] = useState(new Date().getFullYear());

  // Meta
  const [conversionRate, setConversionRate] = useState<number>(1);
  const [emissionFactor, setEmissionFactor] = useState<string>('');
  const [emissionFactorSource, setEmissionFactorSource] = useState<string>('');
  const [standardQuantity, setStandardQuantity] = useState<number | null>(null);
  const [co2ePreview, setCo2ePreview] = useState<number | null>(null);

  // Note: Region and country fetching has been removed

  // Fetch activities
  const { data: activities } = useQuery({
    queryKey: [`/api/scope3/activities`],
    queryFn: getQueryFn(),
  });

  // Fetch meta when activity+unit set
  const { data: activityMeta } = useQuery({
    queryKey: activityType && unit ? [
      `/api/scope3/activity-meta?activity_type=${encodeURIComponent(activityType)}&unit=${encodeURIComponent(unit)}`
    ] : [null],
    queryFn: getQueryFn(),
    enabled: !!activityType && !!unit,
  });

  // Initialize unit when activity changes
  useEffect(() => {
    if (activityType && activities) {
      const found = (activities as any[]).find(a => a.activity_type === activityType);
      if (found?.default_unit) setUnit(found.default_unit);
      else if (found?.units?.length) setUnit(found.units[0]);
    }
  }, [activityType, activities]);

  // Apply meta
  useEffect(() => {
    if (activityMeta) {
      setConversionRate(activityMeta.conversion_rate ?? 1);
      setEmissionFactor(String(activityMeta.emission_factor ?? ''));
      setEmissionFactorSource(activityMeta.emission_factor_source ?? '');
    }
  }, [activityMeta]);

  // Live compute
  useEffect(() => {
    const qty = parseFloat(quantity);
    const ef = parseFloat(emissionFactor);
    const convQty = isFiniteNumber(qty) ? qty * (conversionRate || 1) : NaN;
    setStandardQuantity(isFiniteNumber(convQty) ? convQty : null);
    if (isFiniteNumber(convQty) && isFiniteNumber(ef)) setCo2ePreview(convQty * ef);
    else setCo2ePreview(null);
  }, [quantity, emissionFactor, conversionRate]);

  function isFiniteNumber(n: any) {
    return typeof n === 'number' && isFinite(n);
  }

  // Note: Region change effect has been removed

  const activityOptions = useMemo(() => {
    return ([{ activity_type: '', units: [], default_unit: '' }, ...(activities || [])] as any[])
      .map(a => ({ value: a.activity_type as string, label: a.activity_type as string }));
  }, [activities]);

  const unitOptions = useMemo(() => {
    const found = (activities as any[] | undefined)?.find(a => a.activity_type === activityType);
    const units: string[] = found?.units || [];
    const opts = units.map(u => ({ value: u, label: u }));
    if (unit && !opts.some(o => o.value === unit)) opts.unshift({ value: unit, label: unit });
    return [{ value: '', label: 'Select unit…' }, ...opts];
  }, [activities, activityType, unit]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const qty = parseFloat(quantity);
    const ef = parseFloat(emissionFactor);
    const convQty = isFiniteNumber(qty) ? qty * (conversionRate || 1) : 0;
    const computedCO2e = isFiniteNumber(convQty) && isFiniteNumber(ef) ? convQty * ef : undefined;

    onSubmit({
      scope: 'scope3',
      activity_type: activityType,
      quantity: qty,
      unit: unit,
      emission_factor: ef,
      emission_factor_source: emissionFactorSource || undefined,
      is_estimated: false,
      // Note: region has been removed
      country,
      reporting_year: reportingYear,
      standard_value: convQty,
      co2_equivalent: computedCO2e,
      notes: undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Country"
          value={country}
          options={[
            { value: '', label: 'Select country…' },
            ...(companyCountries || []).map((c: Country) => ({ value: c.code, label: c.name }))
          ]}
          onChange={(e: any) => setCountry(e?.target ? e.target.value : e)}
        />
        <Select
          label="Activity type"
          value={activityType}
          options={[{ value: '', label: 'Select activity…' }, ...activityOptions]}
          onChange={(e: any) => setActivityType(e?.target ? e.target.value : e)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Unit"
          value={unit}
          options={unitOptions}
          onChange={(e: any) => setUnit(e?.target ? e.target.value : e)}
        />
        <Input
          label="Quantity"
          type="number"
          step="0.0001"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Reporting Year"
          type="number"
          min="2000"
          max="2100"
          value={reportingYear}
          onChange={(e) => setReportingYear(parseInt(e.target.value) || new Date().getFullYear())}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Emission factor (kg CO2e per standard unit)"
          placeholder="Auto-fetched"
          type="number"
          step="0.0001"
          value={emissionFactor}
          onChange={(e) => setEmissionFactor(e.target.value)}
          required
        />
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Standard Quantity (Quantity × conversion)</label>
          <div className="text-sm text-gray-600 border border-dashed rounded-md p-2">
            {standardQuantity !== null ? standardQuantity.toFixed(4) : '—'}
          </div>
        </div>
      </div>

      {co2ePreview !== null && (
        <div className="text-lg font-bold text-green-800 bg-green-50 px-4 py-3 rounded-lg border-2 border-green-200">
          Preview CO₂e: {co2ePreview.toFixed(4)} kg CO₂e
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting…' : 'Save Measured Entry'}
        </Button>
      </div>
    </form>
  );
}
